import React, { useCallback, useMemo } from 'react';

import { cn } from '../../lib/utils';
import { useGetFirmBillingCodeStatsQuery } from '../../services/api/firmsService';
import { SimplifiedBillingCode } from '../../services/types/client-intake-types';
import { getBillingGroupsForFirm } from './billingConfig';
import { FormSection } from './FormSection';

interface BillingSectionProps {
  selectedBillingCode: SimplifiedBillingCode | null | undefined;
  setValue: (
    field: 'simplified_billing_code',
    value: SimplifiedBillingCode | null,
    options?: { shouldValidate?: boolean },
  ) => void;
  isLoading: boolean;
  /** When provided, billing options are sorted by this firm's usage frequency. */
  firmId?: string;
}

export const BillingSection: React.FC<BillingSectionProps> = ({
  selectedBillingCode,
  setValue,
  isLoading,
  firmId,
}) => {
  const { data: billingStats } = useGetFirmBillingCodeStatsQuery(firmId ?? '', {
    skip: !firmId,
  });

  const billingGroups = useMemo(
    () => getBillingGroupsForFirm(billingStats),
    [billingStats],
  );

  const handleBillingCodeClick = useCallback(
    (code: SimplifiedBillingCode) => {
      if (selectedBillingCode === code) {
        setValue('simplified_billing_code', null, { shouldValidate: true });
      } else {
        setValue('simplified_billing_code', code, { shouldValidate: true });
      }
    },
    [selectedBillingCode, setValue],
  );

  return (
    <FormSection
      title="Billing"
      description="Select the billing classification for this matter"
    >
      <div className="space-y-4">
        {billingGroups.map((section) => (
          <div
            key={section.title}
            className="rounded-lg border border-zinc-200 dark:border-zinc-800 overflow-hidden"
          >
            <div className="px-3 py-1.5 bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-800">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                {section.title}
              </span>
            </div>
            <div className="bg-white dark:bg-zinc-900">
              {section.options.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  disabled={isLoading}
                  onClick={() => handleBillingCodeClick(option.code)}
                  className={cn(
                    'flex items-center gap-3 w-full px-3 py-2 text-left transition-colors',
                    'border-b border-zinc-100 dark:border-zinc-800 last:border-b-0',
                    selectedBillingCode === option.code
                      ? 'bg-indigo-50 dark:bg-indigo-950/30'
                      : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
                    isLoading && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <div
                    className={cn(
                      'h-4 w-4 rounded-full border-2 flex-shrink-0 transition-colors',
                      selectedBillingCode === option.code
                        ? 'border-indigo-600 bg-indigo-600'
                        : 'border-zinc-300 dark:border-zinc-600',
                    )}
                  >
                    {selectedBillingCode === option.code && (
                      <div className="h-full w-full flex items-center justify-center">
                        <div className="h-1.5 w-1.5 rounded-full bg-white" />
                      </div>
                    )}
                  </div>
                  <span className="text-sm font-medium text-zinc-900 dark:text-white min-w-32">
                    {option.label}
                  </span>
                  <div className="flex flex-col">
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">
                      {option.description}
                    </span>
                    {option.note && (
                      <span className="mt-1 inline-flex self-start text-[10px] font-medium text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded">
                        {option.note}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </FormSection>
  );
};
